import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Trash2, Building2, Search, Package,
  Calculator, FileText
} from 'lucide-react';

const CreateQuotation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Customer
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  // Products
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  
  // Quotation items
  const [items, setItems] = useState([]);
  
  // Quotation details
  const [quotationDate, setQuotationDate] = useState(new Date().toISOString().split('T')[0]);
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [termsConditions, setTermsConditions] = useState(
    '1. This quotation is valid for 30 days from the date of issue.\n' +
    '2. Prices are subject to change without prior notice.\n' +
    '3. Delivery within 7-10 working days after confirmation.\n' +
    '4. 50% advance payment required to confirm the order.'
  );
  
  // Loading
  const [submitting, setSubmitting] = useState(false);

  // Role checks
  const isAdmin = user?.role === 'Admin';
  const isBackOfficeAccounts = user?.role === 'Backoffice Accounts Executive';
  const canAccess = isAdmin || isBackOfficeAccounts;

  // Fetch customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await api.get('/customers', { params: { status: 'Active' } });
        setCustomers(response.data);
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };
    fetchCustomers();
  }, []);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get('/products', { params: { status: 'Active' } });
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchProducts();
  }, []);

  // Filter customers based on search
  const filteredCustomers = customers.filter(c => 
    c.company_name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.mobile?.includes(customerSearch) ||
    c.contact_person?.toLowerCase().includes(customerSearch.toLowerCase())
  ).slice(0, 10);

  // Filter products based on search
  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.code?.toLowerCase().includes(productSearch.toLowerCase())
  ).slice(0, 10);

  // Handle customer selection
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.company_name);
    setShowCustomerDropdown(false);
  };

  // Add product item
  const addProduct = (product) => {
    const newItem = {
      id: `product-${product.id}-${Date.now()}`,
      item_type: 'product',
      product_id: product.id,
      description: product.name,
      hsn_code: product.hsn_code || '',
      quantity: 1,
      unit_price: product.selling_price || 0,
      discount_percent: 0,
      cgst_percent: 9,
      sgst_percent: 9
    };
    
    setItems(prev => [...prev, newItem]);
    setProductSearch('');
  };

  // Add custom item
  const addCustomItem = () => {
    const newItem = {
      id: `custom-${Date.now()}`,
      item_type: 'service',
      description: '',
      hsn_code: '',
      quantity: 1,
      unit_price: 0,
      discount_percent: 0,
      cgst_percent: 9,
      sgst_percent: 9
    };
    setItems(prev => [...prev, newItem]);
  };

  // Update item
  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(item => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  // Remove item
  const removeItem = (id) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  // Calculate item totals
  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.unit_price;
    const discount = subtotal * (item.discount_percent / 100);
    const taxable = subtotal - discount;
    const cgst = taxable * (item.cgst_percent / 100);
    const sgst = taxable * (item.sgst_percent / 100);
    return {
      subtotal,
      discount,
      taxable,
      cgst,
      sgst,
      total: taxable + cgst + sgst
    };
  };

  // Calculate quotation totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalDiscount = 0;
    let totalCgst = 0;
    let totalSgst = 0;
    
    items.forEach(item => {
      const itemTotals = calculateItemTotal(item);
      subtotal += itemTotals.subtotal;
      totalDiscount += itemTotals.discount;
      totalCgst += itemTotals.cgst;
      totalSgst += itemTotals.sgst;
    });
    
    const taxableAmount = subtotal - totalDiscount;
    const netAmount = taxableAmount + totalCgst + totalSgst;
    
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      totalDiscount: Math.round(totalDiscount * 100) / 100,
      taxableAmount: Math.round(taxableAmount * 100) / 100,
      totalCgst: Math.round(totalCgst * 100) / 100,
      totalSgst: Math.round(totalSgst * 100) / 100,
      totalTax: Math.round((totalCgst + totalSgst) * 100) / 100,
      netAmount: Math.round(netAmount * 100) / 100
    };
  };

  const totals = calculateTotals();

  // Submit quotation
  const handleSubmit = async () => {
    // Validations
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    
    if (items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    // Check for empty descriptions
    const emptyItem = items.find(i => !i.description || i.description.trim() === '');
    if (emptyItem) {
      toast.error('All items must have a description');
      return;
    }
    
    setSubmitting(true);
    try {
      const quotationData = {
        customer_id: selectedCustomer.id,
        quotation_date: quotationDate,
        valid_until: validUntil || null,
        items: items.map(item => ({
          item_type: item.item_type,
          product_id: item.product_id || null,
          service_id: item.service_id || null,
          description: item.description,
          hsn_code: item.hsn_code || '',
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          discount_percent: parseFloat(item.discount_percent) || 0,
          cgst_percent: parseFloat(item.cgst_percent) || 0,
          sgst_percent: parseFloat(item.sgst_percent) || 0
        })),
        notes: notes || '',
        terms_conditions: termsConditions || ''
      };
      
      const response = await api.post('/sales-billing/quotations', quotationData);
      toast.success(`Quotation created successfully - ${response.data.quotation?.quotation_no}`);
      navigate('/sales-billing');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quotation');
    } finally {
      setSubmitting(false);
    }
  };

  if (!canAccess) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="text-red-600">Access Denied</CardTitle>
            <CardDescription>
              Only Admin and Backoffice Accounts Executive can create quotations.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="create-quotation-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/sales-billing')}>
          <ArrowLeft size={18} />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Create Quotation</h1>
          <p className="text-gray-600 mt-1">Generate a new quotation for customer</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details & Items */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 size={20} />
                Customer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    placeholder="Search customer by name, mobile..."
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                      if (!e.target.value) setSelectedCustomer(null);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    className="pl-10"
                    data-testid="customer-search"
                  />
                </div>
                
                {/* Customer Dropdown */}
                {showCustomerDropdown && customerSearch && filteredCustomers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredCustomers.map(customer => (
                      <div
                        key={customer.id}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                        onClick={() => handleSelectCustomer(customer)}
                      >
                        <p className="font-medium">{customer.company_name}</p>
                        <p className="text-sm text-gray-500">
                          {customer.mobile} • {customer.city}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Customer Details */}
              {selectedCustomer && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Company:</span>
                      <span className="ml-2 font-medium">{selectedCustomer.company_name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Contact:</span>
                      <span className="ml-2">{selectedCustomer.contact_person}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Mobile:</span>
                      <span className="ml-2">{selectedCustomer.mobile}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">City:</span>
                      <span className="ml-2">{selectedCustomer.city}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Add Products */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package size={20} />
                Add Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    placeholder="Search product by name or code..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="pl-10"
                    data-testid="product-search"
                  />
                </div>
                
                {/* Products Dropdown */}
                {productSearch && filteredProducts.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredProducts.map(product => (
                      <div
                        key={product.id}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                        onClick={() => addProduct(product)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">{product.name}</p>
                            <p className="text-sm text-gray-500">{product.code}</p>
                          </div>
                          <p className="font-semibold text-green-700">₹{(product.selling_price || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <Button variant="outline" className="mt-4" onClick={addCustomItem}>
                <Plus size={16} className="mr-2" />
                Add Custom Item
              </Button>
            </CardContent>
          </Card>

          {/* Quotation Items */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator size={20} />
                Quotation Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              {items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No items added yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[300px]">Description</TableHead>
                        <TableHead>HSN</TableHead>
                        <TableHead className="w-20">Qty</TableHead>
                        <TableHead className="w-28">Rate (₹)</TableHead>
                        <TableHead className="w-20">Disc %</TableHead>
                        <TableHead className="w-20">CGST %</TableHead>
                        <TableHead className="w-20">SGST %</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item) => {
                        const itemTotals = calculateItemTotal(item);
                        return (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Input
                                value={item.description}
                                onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                                placeholder="Item description"
                                className="min-w-[200px]"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={item.hsn_code}
                                onChange={(e) => updateItem(item.id, 'hsn_code', e.target.value)}
                                placeholder="HSN"
                                className="w-20"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                                min="1"
                                className="w-16"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.unit_price}
                                onChange={(e) => updateItem(item.id, 'unit_price', e.target.value)}
                                min="0"
                                className="w-24"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.discount_percent}
                                onChange={(e) => updateItem(item.id, 'discount_percent', e.target.value)}
                                min="0"
                                max="100"
                                className="w-16"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.cgst_percent}
                                onChange={(e) => updateItem(item.id, 'cgst_percent', e.target.value)}
                                min="0"
                                className="w-16"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.sgst_percent}
                                onChange={(e) => updateItem(item.id, 'sgst_percent', e.target.value)}
                                min="0"
                                className="w-16"
                              />
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              ₹{itemTotals.total.toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeItem(item.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Terms & Conditions */}
          <Card>
            <CardHeader>
              <CardTitle>Terms & Conditions</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={termsConditions}
                onChange={(e) => setTermsConditions(e.target.value)}
                rows={5}
                placeholder="Enter terms and conditions..."
              />
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Summary & Actions */}
        <div className="space-y-6">
          {/* Quotation Details */}
          <Card>
            <CardHeader>
              <CardTitle>Quotation Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Quotation Date *</Label>
                <Input
                  type="date"
                  value={quotationDate}
                  onChange={(e) => setQuotationDate(e.target.value)}
                />
              </div>
              <div>
                <Label>Valid Until</Label>
                <Input
                  type="date"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  min={quotationDate}
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Quotation Summary */}
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardHeader>
              <CardTitle className="text-purple-800">Quotation Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span>₹{totals.subtotal.toLocaleString()}</span>
                </div>
                
                {totals.totalDiscount > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Discount:</span>
                    <span>-₹{totals.totalDiscount.toLocaleString()}</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Taxable Amount:</span>
                  <span>₹{totals.taxableAmount.toLocaleString()}</span>
                </div>
                
                <div className="flex justify-between text-gray-600">
                  <span>CGST:</span>
                  <span>₹{totals.totalCgst.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>SGST:</span>
                  <span>₹{totals.totalSgst.toLocaleString()}</span>
                </div>
                
                <div className="border-t pt-3 flex justify-between text-lg font-bold text-purple-800">
                  <span>Total:</span>
                  <span>₹{totals.netAmount.toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              className="w-full bg-purple-600 hover:bg-purple-700 h-12 text-lg"
              onClick={handleSubmit}
              disabled={submitting || items.length === 0 || !selectedCustomer}
              data-testid="submit-quotation"
            >
              {submitting ? 'Creating...' : 'Create Quotation'}
            </Button>
            
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/sales-billing')}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateQuotation;
